/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if email is valid
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if phone is valid
 */
export const validatePhone = (phone) => {
  const phoneRegex = /^\+?[1-9]\d{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-().]/g, ''));
};

/**
 * Validate required field
 * @param {*} value - Value to validate
 * @returns {boolean} True if value is not empty
 */
export const validateRequired = (value) => {
  return value !== null && value !== undefined && value.toString().trim() !== '';
};

/**
 * Validate minimum length
 * @param {string} value - String to validate
 * @param {number} minLength - Minimum required length
 * @returns {boolean} True if string meets minimum length
 */
export const validateMinLength = (value, minLength) => {
  return value && value.toString().length >= minLength;
};

/**
 * Validate maximum length
 * @param {string} value - String to validate
 * @param {number} maxLength - Maximum allowed length
 * @returns {boolean} True if string is within maximum length
 */
export const validateMaxLength = (value, maxLength) => {
  return !value || value.toString().length <= maxLength;
};

/**
 * Validate numeric value
 * @param {*} value - Value to validate
 * @returns {boolean} True if value is numeric
 */
export const validateNumeric = (value) => {
  return !Number.isNaN(Number(value)) && !Number.isNaN(Number.parseFloat(value));
};

/**
 * Validate date format (DD-MM-YYYY)
 * @param {string} date - Date string to validate
 * @returns {boolean} True if date is valid
 */
export const validateDate = (date) => {
  if (!date) return false;
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) return false;
  
  const d = new Date(date);
  return d instanceof Date && !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === date;
};

/**
 * Validate salary amount
 * @param {*} value - Salary value to validate
 * @returns {boolean} True if salary is valid
 */
export const validateSalary = (value) => {
  if (!validateNumeric(value)) return false;
  const num = Number.parseFloat(value);
  const decimalPart = num.toString().split('.')[1];
  return num >= 0 && (decimalPart === undefined || decimalPart.length <= 2);
};

export const PASSWORD_RULES_MESSAGE =
  'Password must be at least 8 characters and contain at least one number and one symbol';

/**
 * Validate password complexity — mirrors server/utils/passwordValidator.js.
 * Rules: length >= 8, at least one digit, at least one non-alphanumeric symbol.
 * @param {string} password
 * @returns {{ valid: boolean, error?: string }}
 */
export const validatePasswordComplexity = (password) => {
  if (typeof password !== 'string' || password.length < 8) {
    return { valid: false, error: PASSWORD_RULES_MESSAGE };
  }
  if (!/\d/.test(password)) {
    return { valid: false, error: PASSWORD_RULES_MESSAGE };
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    return { valid: false, error: PASSWORD_RULES_MESSAGE };
  }
  return { valid: true };
}; 